import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { zahlungenApi, statistikenApi } from '../api';
import { format, addDays, addMonths, startOfDay, endOfDay, isWithinInterval, parseISO, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';

function monatlich(z) {
  switch(z.intervall) {
    case 'monatlich': return z.betrag;
    case 'vierteljaehrlich': return z.betrag / 3;
    case 'halbjaehrlich': return z.betrag / 6;
    case 'jaehrlich': return z.betrag / 12;
    default: return z.betrag;
  }
}

function naechsteZahlungen(z, von, bis) {
  const ergebnisse = [];
  const start = new Date(von);
  const ende = new Date(bis);
  let datum = z.startdatum ? new Date(z.startdatum) : new Date();

  // Intervall in Tagen
  const intervallTage = {
    'monatlich': 30, 'vierteljaehrlich': 91, 'halbjaehrlich': 182, 'jaehrlich': 365
  }[z.intervall] || 30;

  // Ersten Termin im Zeitraum finden
  while (datum < start) {
    datum = new Date(datum.getTime() + intervallTage * 24 * 60 * 60 * 1000);
  }

  // Alle Termine im Zeitraum sammeln
  let count = 0;
  while (datum <= ende && count < 50) {
    ergebnisse.push({ ...z, faelligAm: new Date(datum) });
    datum = new Date(datum.getTime() + intervallTage * 24 * 60 * 60 * 1000);
    count++;
  }
  return ergebnisse;
}

const ZEITRAEUME = [
  { label: 'Diese Woche', tage: 7 },
  { label: 'Dieser Monat', tage: 30 },
  { label: 'Nächste 3 Monate', tage: 90 },
  { label: 'Nächste 6 Monate', tage: 180 },
  { label: 'Dieses Jahr', tage: 365 },
  { label: 'Benutzerdefiniert', tage: -1 },
];

const KATEGORIEN = ['Alle', 'Sparvertrag', 'Kredit', 'Versicherung', 'Abo', 'Vertragskosten'];
const TYP_FARBEN = { 'Sparvertrag': '#10b981', 'Kredit': '#ef4444', 'Versicherung': '#3b82f6', 'Abo': '#8b5cf6', 'Vertragskosten': '#6366f1' };
const TYP_ICONS = { 'Sparvertrag': '💰', 'Kredit': '🏦', 'Versicherung': '🛡️', 'Abo': '📱', 'Vertragskosten': '📋' };

function eur(v) {
  return (v||0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export default function AusgabenplanerPage() {
  const navigate = useNavigate();
  const [zahlungen, setZahlungen] = useState([]);
  const [vertraege, setVertraege] = useState([]);
  const [laden, setLaden] = useState(true);
  const [zeitraum, setZeitraum] = useState(1); // Index in ZEITRAEUME
  const [vonDatum, setVonDatum] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [bisDatum, setBisDatum] = useState(format(addDays(new Date(), 30), 'yyyy-MM-dd'));
  const [kategorie, setKategorie] = useState('Alle');
  const [richtung, setRichtung] = useState('alle');
  const [ansicht, setAnsicht] = useState('liste'); // liste | kalender

  useEffect(() => { datenLaden(); }, []);

  async function datenLaden() {
    setLaden(true);
    try {
      const [z, s] = await Promise.all([
        zahlungenApi.list(),
        statistikenApi.get()
      ]);
      setZahlungen(z.data || []);
      setVertraege(s.data?.vertraege || []);
    } catch(e) { console.error(e); }
    setLaden(false);
  }

  function zeitraumWaehlen(idx) {
    setZeitraum(idx);
    if (ZEITRAEUME[idx].tage > 0) {
      setVonDatum(format(new Date(), 'yyyy-MM-dd'));
      setBisDatum(format(addDays(new Date(), ZEITRAEUME[idx].tage), 'yyyy-MM-dd'));
    }
  }

  // Alle anstehenden Zahlungen berechnen
  const von = new Date(vonDatum);
  const bis = new Date(bisDatum);

  let alleZahlungen = [];

  // Aus Zahlungsmodul
  zahlungen.filter(z => z.status === 'aktiv').forEach(z => {
    const termine = naechsteZahlungen(z, von, bis);
    termine.forEach(t => {
      alleZahlungen.push({
        id: z.id + '_' + t.faelligAm.getTime(),
        name: z.name,
        anbieter: z.anbieter,
        betrag: z.betrag,
        typ: z.typ,
        richtung: z.richtung,
        faelligAm: t.faelligAm,
        kategorie: z.typ,
        farbe: TYP_FARBEN[z.typ] || '#6366f1',
        icon: TYP_ICONS[z.typ] || '💳',
      });
    });
  });

  // Filter anwenden
  let gefiltert = alleZahlungen;
  if (kategorie !== 'Alle') gefiltert = gefiltert.filter(z => z.kategorie === kategorie);
  if (richtung === 'ausgabe') gefiltert = gefiltert.filter(z => z.richtung === 'ausgabe');
  if (richtung === 'einzahlung') gefiltert = gefiltert.filter(z => z.richtung === 'einzahlung');

  // Sortieren nach Datum
  gefiltert.sort((a, b) => a.faelligAm - b.faelligAm);

  // Summen
  const summeAusgaben = gefiltert.filter(z => z.richtung === 'ausgabe').reduce((s, z) => s + z.betrag, 0);
  const summeEinzahlungen = gefiltert.filter(z => z.richtung === 'einzahlung').reduce((s, z) => s + z.betrag, 0);
  const saldo = summeEinzahlungen - summeAusgaben;

  // Nach Datum gruppieren
  const gruppiertNachDatum = gefiltert.reduce((acc, z) => {
    const key = format(z.faelligAm, 'yyyy-MM-dd');
    if (!acc[key]) acc[key] = [];
    acc[key].push(z);
    return acc;
  }, {});

  const heute = new Date();

  return (
    <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>←</button>
        <h1 style={{ margin: 0, fontSize: '26px' }}>📅 Ausgabenplaner</h1>
      </div>

      {/* Zeitraum-Filter */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ fontWeight: '600', marginBottom: '10px', color: '#374151' }}>⏱️ Zeitraum</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
          {ZEITRAEUME.map((z, i) => (
            <button key={i} onClick={() => zeitraumWaehlen(i)}
              style={{ padding: '6px 14px', border: 'none', borderRadius: '99px', cursor: 'pointer', fontSize: '13px',
                background: zeitraum === i ? '#6366f1' : '#f1f5f9', color: zeitraum === i ? 'white' : '#374151', fontWeight: zeitraum === i ? '600' : '400' }}>
              {z.label}
            </button>
          ))}
        </div>
        {zeitraum === 5 && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <input type="date" value={vonDatum} onChange={e => setVonDatum(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }} />
            <span style={{ color: '#64748b' }}>bis</span>
            <input type="date" value={bisDatum} onChange={e => setBisDatum(e.target.value)}
              style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }} />
          </div>
        )}
      </div>

      {/* Kategorie & Richtung Filter */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151', fontSize: '13px' }}>🏷️ Kategorie</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {KATEGORIEN.map(k => (
                <button key={k} onClick={() => setKategorie(k)}
                  style={{ padding: '4px 12px', border: 'none', borderRadius: '99px', cursor: 'pointer', fontSize: '12px',
                    background: kategorie === k ? '#6366f1' : '#f1f5f9', color: kategorie === k ? 'white' : '#374151' }}>
                  {TYP_ICONS[k] || '📋'} {k}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '600', marginBottom: '8px', color: '#374151', fontSize: '13px' }}>↕️ Richtung</div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[['alle', '🔄 Alle'], ['ausgabe', '📤 Ausgaben'], ['einzahlung', '📥 Einzahlungen']].map(([val, label]) => (
                <button key={val} onClick={() => setRichtung(val)}
                  style={{ padding: '4px 12px', border: 'none', borderRadius: '99px', cursor: 'pointer', fontSize: '12px',
                    background: richtung === val ? '#6366f1' : '#f1f5f9', color: richtung === val ? 'white' : '#374151' }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPI-Zusammenfassung */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
        <div style={{ background: '#fee2e2', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626' }}>{eur(summeAusgaben)}</div>
          <div style={{ fontSize: '11px', color: '#ef4444', marginTop: '4px' }}>📤 Ausgaben gesamt</div>
        </div>
        <div style={{ background: '#dcfce7', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#15803d' }}>{eur(summeEinzahlungen)}</div>
          <div style={{ fontSize: '11px', color: '#16a34a', marginTop: '4px' }}>📥 Einzahlungen gesamt</div>
        </div>
        <div style={{ background: saldo >= 0 ? '#dcfce7' : '#fee2e2', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: saldo >= 0 ? '#15803d' : '#dc2626' }}>{eur(saldo)}</div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>⚖️ Saldo</div>
        </div>
      </div>

      {/* Zahlungsliste nach Datum gruppiert */}
      {laden ? <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>⏳ Laden...</div>
        : gefiltert.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
            <div style={{ fontSize: '16px' }}>Keine Zahlungen im gewählten Zeitraum</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {Object.entries(gruppiertNachDatum).map(([datum, zahlungenAmTag]) => {
              const datumObj = new Date(datum);
              const istHeute = format(datumObj, 'yyyy-MM-dd') === format(heute, 'yyyy-MM-dd');
              const istVergangen = datumObj < heute && !istHeute;
              const tageVon = differenceInDays(datumObj, heute);

              return (
                <div key={datum}>
                  {/* Datums-Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div style={{ background: istHeute ? '#6366f1' : istVergangen ? '#ef4444' : '#f1f5f9',
                      color: istHeute || istVergangen ? 'white' : '#374151',
                      padding: '4px 12px', borderRadius: '99px', fontSize: '13px', fontWeight: '600' }}>
                      {istHeute ? '📌 Heute' : istVergangen ? '⚠️ Überfällig' : tageVon === 1 ? '⏰ Morgen' : `📅 In ${tageVon} Tagen`}
                    </div>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>
                      {format(datumObj, 'EEEE, dd. MMMM yyyy', { locale: de })}
                    </span>
                    <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                    <span style={{ color: '#374151', fontWeight: '600', fontSize: '13px' }}>
                      {eur(zahlungenAmTag.reduce((s, z) => s + (z.richtung === 'ausgabe' ? z.betrag : 0), 0))}
                    </span>
                  </div>

                  {/* Zahlungen des Tages */}
                  {zahlungenAmTag.map((z, i) => (
                    <div key={i} style={{ background: 'white', borderRadius: '10px', padding: '14px 16px',
                      marginBottom: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      borderLeft: `4px solid ${z.farbe}`,
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ fontSize: '24px' }}>{z.icon}</div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{z.name}</div>
                          <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                            {z.anbieter && <span style={{ color: '#64748b', fontSize: '12px' }}>{z.anbieter}</span>}
                            <span style={{ background: z.farbe + '20', color: z.farbe, padding: '1px 8px',
                              borderRadius: '99px', fontSize: '11px', fontWeight: '600' }}>{z.kategorie}</span>
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: '700', fontSize: '16px',
                          color: z.richtung === 'ausgabe' ? '#ef4444' : '#10b981' }}>
                          {z.richtung === 'ausgabe' ? '-' : '+'}{eur(z.betrag)}
                        </div>
                        <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                          {z.richtung === 'ausgabe' ? '📤 Ausgabe' : '📥 Einzahlung'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}
